






























import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  ssrRef,
  useContext,
  useFetch,
  useMeta
} from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import { onSSR } from '@vue-storefront/core';
import NostoCategory from 'atoms/NostoCategory.vue';

import {
  useCanonical,
  usePreviewMode,
  useRequestPath,
  useStructuredData
} from '~/diptyqueTheme/composable';
import { useContentfulPreview } from '~/diptyqueTheme/composable/useContentfulPreview';
import { useHreflangs } from '~/diptyqueTheme/composable/useHreflangs';
import { Logger } from '~/helpers/logger';
import { useCategoryStore } from '~/modules/catalog/category/stores/category';
import { CategoryTypeEnum } from '~/modules/catalog/product/enums/CategoryTypeEnum';
import type {
  CategoryListingLayoutEnum,
  CategoryTree
} from '~/modules/GraphQL/types';

export default defineComponent({
  name: 'CategoryPage',
  components: {
    VaimoBreadcrumbs: () => import('atoms/VaimoBreadcrumbs.vue'),
    CategoryDefault: () => import('templates/category/CategoryDefault.vue'),
    CategoryCollection: () =>
      import('templates/category/CategoryCollection.vue'),
    CategoryMain: () => import('templates/category/CategoryMain.vue'),
    NostoCategory
  },
  setup() {
    const {
      getFullCategoryBasedOnRequestPath,
      getBreadcrumbsList,
      getBreadcrumbs
    } = useRequestPath();
    const {
      app: { i18n },
      error: nuxtError
    } = useContext();
    const { addTags } = useCache();
    const activeCategory = ref<CategoryTree | null>(null);
    const loading = ref<boolean | null>(true);
    const noCategoryData = ref<boolean | null>(false);
    const { getCategoryBreadcrumbsStructuredData } = useStructuredData();
    const { getCategoryLink, categoryURL } = useContentfulPreview();
    const { isPreviewMode } = usePreviewMode();
    const { setCategoryType, resetCategoryType } = useCategoryStore();

    const renderer = computed(() => {
      return CategoryTypeEnum.CATEGORY_TREE;
    });

    const breadcrumbs = ssrRef([]);

    onSSR(async () => {
      const breadcrumbsList = await getBreadcrumbsList('category');
      breadcrumbs.value = getBreadcrumbs(breadcrumbsList, true);
    });

    const fetchCategoryData = async () => {
      if (isPreviewMode.value) {
        await getCategoryLink();
      }

      try {
        const result = await getFullCategoryBasedOnRequestPath(
          categoryURL.value
        );
        if (result) {
          activeCategory.value = result;
          setCategoryType(result.listing_layout as CategoryListingLayoutEnum);
        } else {
          noCategoryData.value = true;
          setInitialLoadFromServer(false);
          return nuxtError({ statusCode: 404 });
        }
      } catch (error) {
        noCategoryData.value = true;
        Logger.error(
          'Get Full Product Category from URL path by route [ERROR]',
          error
        );
        Logger.error(error);
        return nuxtError({ statusCode: 404 });
      }
    };

    const initialLoadFromServer = ref(true);
    const { canonicalCategoryLink } = useCanonical();

    const setInitialLoadFromServer = (state: boolean) => {
      initialLoadFromServer.value = state;
    };

    useFetch(async () => {
      loading.value = true;
      setInitialLoadFromServer(process.server);
      await fetchCategoryData();
      loading.value = false;

      const tags = [{ prefix: CacheTagPrefix.View, value: 'category' }];
      addTags(tags);
    });

    const listingLayout = computed(() => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return activeCategory.value?.listing_layout;
    });

    const metaDescription = computed(() => {
      const description = [];

      switch (listingLayout.value) {
        case 'main_category':
          description.push(i18n.t('Discover the universe'));
          break;
        case 'collection':
          description.push(i18n.t('Discover the collection'));
          break;
        default:
          description.push(i18n.t('Discover the products'));
      }

      description.push(activeCategory.value?.name ?? '');
      description.push(
        i18n.t(
          'Diptyque, a precursor of an art of living through the senses, where perfume and art permeate everything.'
        )
      );

      return description.join(' ');
    });

    const metaTags = computed(() =>
      activeCategory.value
        ? {
            title:
              activeCategory.value?.meta_title ||
              `${activeCategory.value?.name} | Diptyque Paris`,
            meta: [
              {
                hid: 'og:title',
                property: 'og:title',
                content:
                  activeCategory.value?.meta_title ||
                  `${activeCategory.value?.name} | Diptyque Paris`
              },
              {
                hid: 'description',
                name: 'description',
                content:
                  activeCategory.value?.meta_description ||
                  metaDescription.value
              },
              {
                hid: 'og:description',
                name: 'og:description',
                content:
                  activeCategory.value?.meta_description ||
                  metaDescription.value
              }
            ],
            link: [canonicalCategoryLink(activeCategory?.value)],
            script: [
              {
                type: 'application/ld+json',
                json: getCategoryBreadcrumbsStructuredData(breadcrumbs.value)
              }
            ]
          }
        : {}
    );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useMeta(() => metaTags.value);

    onMounted(() => {
      fetchCategoryData();
    });

    onUnmounted(() => {
      resetCategoryType();
    });

    const nostoCategoryId = computed(() => {
      let result = '';
      if (breadcrumbs.value) {
        const nostoCrumbs = JSON.parse(JSON.stringify(breadcrumbs.value));
        nostoCrumbs.shift(); // remove home page link
        nostoCrumbs.forEach(
          (crumb) => (result += '/' + crumb.text.toLowerCase())
        );
      }
      return result;
    });

    return {
      loading,
      noCategoryData,
      activeCategory,
      nostoCategoryId,
      renderer,
      listingLayout,
      metaTags,
      breadcrumbs
    };
  },

  async asyncData(ctx) {
    const { getHrefLangs } = useHreflangs(ctx);
    const hrefLangsLinks = await getHrefLangs();
    return {
      hrefLangsLinks
    };
  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  head() {
    return {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      link: this.hrefLangsLinks
    };
  }
});
